import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { ChevronRight } from "lucide-react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import type { ComponentProps, KeyboardEvent, ReactNode } from "react";

import { cn } from "helpers/cn";

const Accordion = ({
  className,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Root>) => (
  <AccordionPrimitive.Root
    className={cn("text-body-small leading-5.5 text-black/88", className)}
    data-slot="accordion"
    {...props}
  />
);

const AccordionItem = (props: ComponentProps<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item data-slot="accordion-item" {...props} />
);

const toggleOnEnterOrSpace = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.target !== event.currentTarget || event.repeat) return;
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  event.currentTarget.click();
};

const accordionArrowVariants = cva("flex h-5.5 items-center", {
  variants: {
    arrowPlacement: {
      start: "me-3",
      end: "order-1 ms-3",
      none: "hidden"
    }
  },
  defaultVariants: { arrowPlacement: "start" }
});

type AccordionTriggerProps = Omit<
  ComponentProps<typeof AccordionPrimitive.Trigger>,
  "asChild"
> &
  VariantProps<typeof accordionArrowVariants> & {
    extra?: ReactNode;
  };

const AccordionTrigger = ({
  className,
  children,
  extra,
  arrowPlacement,
  ...props
}: AccordionTriggerProps) => (
  <AccordionPrimitive.Trigger asChild {...props}>
    <div
      className={cn(
        "group/accordion-trigger flex cursor-pointer items-center focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-brand/30",
        className
      )}
      data-slot="accordion-trigger"
      role="button"
      tabIndex={0}
      onKeyDown={toggleOnEnterOrSpace}
    >
      <span
        className={accordionArrowVariants({ arrowPlacement })}
        data-slot="accordion-arrow"
      >
        <ChevronRight className="size-3 text-brand transition-transform duration-300 group-data-[state=open]/accordion-trigger:rotate-90" />
      </span>
      <div className="min-w-0 flex-auto">{children}</div>
      {extra && <div data-slot="accordion-extra">{extra}</div>}
    </div>
  </AccordionPrimitive.Trigger>
);

const AccordionContent = ({
  className,
  children,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Content>) => (
  <AccordionPrimitive.Content
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    data-slot="accordion-content"
    {...props}
  >
    <div className={cn("px-4 py-1.5", className)}>{children}</div>
  </AccordionPrimitive.Content>
);

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
